import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, X, Pencil, Save } from "lucide-react";
import { toast } from "sonner";
import { API, getEmployeeToken, getToken } from "@/lib/api";

/**
 * Shared Inventory page — used by both admin and employee.
 * Pass `mode` = "admin" | "employee" to toggle behaviors.
 * Admin can delete, employees cannot.
 */
export default function InventoryPage({ mode = "employee", apiClient }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [editing, setEditing] = useState(null); // {mode:'create'|'edit', item?}
  const [previewSrc, setPreviewSrc] = useState(null);

  const token = mode === "admin" ? getToken() : getEmployeeToken();

  async function load() {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/inventory");
      setItems(data || []);
    } catch { toast.error("Could not load inventory"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function remove(id) {
    if (mode !== "admin") return;
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/inventory/${id}`);
      toast.success("Item deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  }

  const sections = ["Equipment", "Decor"];
  const visible = filter === "All" ? items : items.filter((i) => i.section === filter);
  const grouped = sections.reduce((acc, s) => { acc[s] = visible.filter((i) => i.section === s); return acc; }, {});

  function photoSrc(it) {
    if (!it.photo_path) return null;
    return `${API}/inventory/${it.id}/photo?auth=${encodeURIComponent(token || "")}`;
  }

  return (
    <div data-testid={`${mode}-inventory-page`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Catering</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Inventory</h1>
          <p className="text-white/50 mt-2 text-sm">{items.length} items total</p>
        </div>
        <div className="flex items-center gap-2">
          {["All", ...sections].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`inv-filter-${s}`}
              className={`text-[10px] uppercase tracking-wider px-3 py-2 border ${filter === s ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10" : "border-white/15 text-white/70 hover:border-white/40"}`}
            >{s}</button>
          ))}
          <button
            onClick={() => setEditing({ mode: "create" })}
            data-testid="inv-add-btn"
            className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]"
          >
            <Plus size={14} /> Add Item
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : (
        <div className="space-y-10">
          {sections.filter((s) => filter === "All" || filter === s).map((section) => (
            <div key={section} data-testid={`inv-section-${section}`}>
              <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-4 border-b border-white/10 pb-2">{section}</h2>
              {grouped[section].length === 0 ? (
                <div className="text-white/40 text-xs">No {section.toLowerCase()} items yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {grouped[section].map((it) => (
                    <div key={it.id} className="bg-[#0e0e0e] border border-white/10 flex flex-col" data-testid={`inv-item-${it.id}`}>
                      <button
                        type="button"
                        onClick={() => photoSrc(it) && setPreviewSrc(photoSrc(it))}
                        className="aspect-square bg-[#0a0a0a] border-b border-white/5 overflow-hidden cursor-zoom-in"
                      >
                        {photoSrc(it) ? (
                          <img src={photoSrc(it)} alt={it.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/20 text-[10px] uppercase tracking-wider">No photo</div>
                        )}
                      </button>
                      <div className="p-3 flex-1 flex flex-col">
                        <div className="text-white text-sm font-medium truncate">{it.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">
                          {it.available_quantity}/{it.quantity} available
                        </div>
                        {it.notes && <div className="text-xs text-white/50 mt-1 line-clamp-2">{it.notes}</div>}
                        <div className="flex items-center justify-end gap-1 mt-auto pt-3">
                          <button
                            onClick={() => setEditing({ mode: "edit", item: it })}
                            data-testid={`inv-edit-${it.id}`}
                            className="p-1.5 text-white/60 hover:text-[#C9963F]"
                            title="Edit"
                          ><Pencil size={14} /></button>
                          {mode === "admin" && (
                            <button
                              onClick={() => remove(it.id)}
                              data-testid={`inv-delete-${it.id}`}
                              className="p-1.5 text-white/60 hover:text-red-400"
                              title="Delete"
                            ><Trash2 size={14} /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <InventoryDialog
          initial={editing.item}
          mode={editing.mode}
          apiClient={apiClient}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {previewSrc && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6" onClick={() => setPreviewSrc(null)}>
          <button className="absolute top-5 right-5 text-white/70 hover:text-white" onClick={() => setPreviewSrc(null)}><X size={24} /></button>
          <img src={previewSrc} alt="preview" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function InventoryDialog({ initial, mode, apiClient, onCancel, onSaved }) {
  const [form, setForm] = useState({
    section: initial?.section || "Equipment",
    name: initial?.name || "",
    quantity: initial?.quantity ?? 0,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState(initial || null);
  const fileRef = useRef(null);
  const token = getEmployeeToken() || getToken();

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      let res;
      if (mode === "edit" && item) {
        res = await apiClient.put(`/inventory/${item.id}`, form);
      } else {
        res = await apiClient.post("/inventory", form);
      }
      setItem(res.data);
      toast.success(mode === "edit" ? "Item updated" : "Item created");
      // If editing or just created, allow photo upload too without closing.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function uploadPhoto(file) {
    if (!item) { toast.error("Save the item first"); return; }
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post(`/inventory/${item.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItem(data);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  const inputCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-[#0e0e0e] border border-[#C9963F]/40 max-w-md w-full p-6 sm:p-8 relative my-8" onClick={(e) => e.stopPropagation()} data-testid="inv-dialog">
        <button onClick={onCancel} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">{mode === "edit" ? "Edit" : "New"} Inventory Item</div>
        <h3 className="font-serif-brand text-2xl mb-6">{form.name || "Untitled"}</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Section</label>
            <div className="flex gap-2">
              {["Equipment", "Decor"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => set("section", s)}
                  data-testid={`inv-section-${s}-btn`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border ${form.section === s ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10" : "border-white/15 text-white/70"}`}
                >{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Name *</label>
            <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="inv-name-input" placeholder="e.g. 6-ft Round Table" autoFocus />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Quantity</label>
            <input type="number" min="0" className={inputCls} value={form.quantity} onChange={(e) => set("quantity", Number(e.target.value))} data-testid="inv-qty-input" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Notes</label>
            <textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="inv-notes-input" placeholder="Color, size, condition…" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Photo</label>
            {item?.photo_path ? (
              <div className="relative inline-block">
                <img src={`${API}/inventory/${item.id}/photo?auth=${encodeURIComponent(token || "")}`} alt="" className="h-32 w-32 object-cover border border-white/10" />
              </div>
            ) : (
              <div className="h-24 w-24 border border-dashed border-white/15 flex items-center justify-center text-[10px] uppercase tracking-wider text-white/30">No photo</div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || !item}
                data-testid="inv-photo-upload-btn"
                className="inline-flex items-center gap-2 border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-1.5 text-xs disabled:opacity-50"
              >
                <Upload size={12} /> {uploading ? "Uploading…" : item?.photo_path ? "Replace Photo" : "Upload Photo"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="inv-photo-input" />
              {!item && <span className="text-[10px] text-white/40">Save the item first to attach a photo.</span>}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className="text-white/60 hover:text-white px-4 py-2 text-sm">Close</button>
          <button onClick={save} disabled={saving} data-testid="inv-save-btn" className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : item ? "Update" : "Create"}
          </button>
        </div>

        {item && (
          <div className="mt-4 text-[10px] uppercase tracking-wider text-white/40">
            Available: {item.available_quantity}/{item.quantity}
          </div>
        )}
        {item && (
          <div className="mt-2 text-right">
            <button onClick={() => onSaved?.()} data-testid="inv-done-btn" className="text-[10px] uppercase tracking-wider text-[#C9963F] hover:text-[#A67A2D]">Done →</button>
          </div>
        )}
      </div>
    </div>
  );
}
